import React from "react";
import {
    Popover,
    Box,
    Typography,
    IconButton,
    Chip,
    Fade
} from "@mui/material";
import CloseIcon from "@mui/icons-material/Close";
import AccessTimeIcon from "@mui/icons-material/AccessTime";
import PersonOutlineIcon from "@mui/icons-material/PersonOutline";
import LocationOnOutlinedIcon from "@mui/icons-material/LocationOnOutlined";
import PhoneOutlinedIcon from "@mui/icons-material/PhoneOutlined";
import EmailOutlinedIcon from "@mui/icons-material/EmailOutlined";
import NotesOutlinedIcon from "@mui/icons-material/NotesOutlined";
import SchoolOutlinedIcon from "@mui/icons-material/SchoolOutlined";
import GoogleCalendarIcon from "../../images/google-calendar.svg";

const STATUS_CONFIG = {
    CONFIRMED: { label: "Confirmed", color: "#E8F5E9", textColor: "#2E7D32" },
    SCHEDULED: { label: "Scheduled", color: "#E3F2FD", textColor: "#1565C0" },
    PENDING: { label: "Pending", color: "#FFF3E0", textColor: "#E65100" },
    CANCELLED: { label: "Cancelled", color: "#FFEBEE", textColor: "#C62828" },
    NOSHOW: { label: "No Show", color: "#FBE9E7", textColor: "#BF360C" },
    DECLINED: { label: "Declined", color: "#FFEBEE", textColor: "#C62828" }
};

const ACCENT_COLORS = {
    booking: "#0087EE",
    blocked: "#78909C",
    googleCal: "#4285F4"
};

function formatTime12h(time24) {
    if (!time24) return "";
    const [h, m] = time24.split(":").map(Number);
    const period = h >= 12 ? "PM" : "AM";
    const h12 = h % 12 || 12;
    return `${h12}:${String(m).padStart(2, "0")} ${period}`;
}

function formatDateNice(dateStr) {
    if (!dateStr) return "";
    try {
        const d = new Date(dateStr + "T12:00:00");
        return d.toLocaleDateString("en-US", {
            weekday: "short",
            month: "short",
            day: "numeric",
            year: "numeric"
        });
    } catch {
        return dateStr;
    }
}

function getInitials(name) {
    if (!name) return "?";
    return name
        .split(" ")
        .map((w) => w[0])
        .join("")
        .toUpperCase()
        .slice(0, 2);
}

function parseNotes(notes) {
    if (!notes) return "";
    if (Array.isArray(notes)) {
        return notes
            .map((n) => n?.notes)
            .filter(Boolean)
            .join(", ");
    }
    if (typeof notes !== "string") return "";
    try {
        const parsed = JSON.parse(notes);
        if (Array.isArray(parsed))
            return parsed
                .map((n) => n?.notes)
                .filter(Boolean)
                .join(", ");
        return notes;
    } catch {
        return notes;
    }
}

/**
 * InfoRow - consistent layout for each detail line.
 */
function InfoRow({ icon: Icon, children, iconSx }) {
    return (
        <Box
            sx={{ display: "flex", alignItems: "flex-start", gap: 1.25, mb: 1 }}
        >
            <Icon
                sx={{
                    fontSize: 18,
                    color: "text.disabled",
                    mt: "1px",
                    flexShrink: 0,
                    ...iconSx
                }}
            />
            <Box sx={{ flex: 1, minWidth: 0 }}>{children}</Box>
        </Box>
    );
}

export default function CalendarTooltip({
    anchorEl,
    event,
    type,
    onClose,
    onEdit,
    onDelete,
    pinned = true,
    onMouseEnter,
    onMouseLeave
}) {
    const open = Boolean(anchorEl) && Boolean(event);
    const isBlocked = type === "blocked";
    const isGoogleCal = event?.source === "googleCal";
    const isCourse = event?.source === "course";

    if (!event) return null;

    const accentColor = isBlocked
        ? ACCENT_COLORS.blocked
        : isGoogleCal
          ? ACCENT_COLORS.googleCal
          : isCourse
            ? "#2E7D32"
            : ACCENT_COLORS.booking;

    const statusConfig =
        isGoogleCal || isCourse ? null : STATUS_CONFIG[event.status] || null;
    const notesText = parseNotes(event.notes);
    const duration = event.serviceDuration || event.minutes || 0;

    return (
        <Popover
            open={open}
            anchorEl={anchorEl}
            onClose={onClose}
            anchorOrigin={{ vertical: "center", horizontal: "right" }}
            transformOrigin={{ vertical: "center", horizontal: "left" }}
            TransitionComponent={Fade}
            transitionDuration={pinned ? 200 : 50}
            disableRestoreFocus
            {...(!pinned && {
                disableScrollLock: true,
                slotProps: {
                    root: {
                        sx: { pointerEvents: "none" }
                    },
                    paper: {
                        sx: {
                            p: 0,
                            width: 340,
                            borderRadius: 3,
                            overflow: "hidden",
                            boxShadow:
                                "0 8px 40px rgba(0,0,0,0.12), 0 2px 8px rgba(0,0,0,0.08)",
                            border: "1px solid rgba(0,0,0,0.06)",
                            pointerEvents: "auto"
                        },
                        onMouseEnter,
                        onMouseLeave
                    }
                }
            })}
            {...(pinned && {
                slotProps: {
                    paper: {
                        sx: {
                            p: 0,
                            width: 340,
                            borderRadius: 3,
                            overflow: "hidden",
                            boxShadow:
                                "0 8px 40px rgba(0,0,0,0.12), 0 2px 8px rgba(0,0,0,0.08)",
                            border: "1px solid rgba(0,0,0,0.06)"
                        }
                    }
                }
            })}
        >
            {/* Accent top bar */}
            <Box sx={{ height: 4, backgroundColor: accentColor }} />

            {/* Header */}
            <Box sx={{ px: 2.5, pt: 2, pb: 1.5 }}>
                <Box
                    sx={{
                        display: "flex",
                        alignItems: "flex-start",
                        justifyContent: "space-between"
                    }}
                >
                    <Box sx={{ flex: 1, mr: 1 }}>
                        {/* Title */}
                        <Typography
                            variant="h6"
                            sx={{
                                fontSize: "1.05rem",
                                fontWeight: 600,
                                lineHeight: 1.3,
                                color: "text.primary"
                            }}
                        >
                            {isBlocked
                                ? "Blocked Time"
                                : isGoogleCal
                                  ? event.MetaData?.eventTitle ||
                                    "Google Calendar Event"
                                  : isCourse
                                    ? event.servicetypeName || "Course Session"
                                    : event.servicetypeName || "Booking"}
                        </Typography>

                        {/* Status + source chips */}
                        <Box
                            sx={{
                                display: "flex",
                                gap: 0.75,
                                mt: 0.75,
                                flexWrap: "wrap"
                            }}
                        >
                            {statusConfig && (
                                <Chip
                                    label={statusConfig.label}
                                    size="small"
                                    sx={{
                                        height: 22,
                                        fontSize: "0.7rem",
                                        fontWeight: 600,
                                        backgroundColor: statusConfig.color,
                                        color: statusConfig.textColor,
                                        border: "none"
                                    }}
                                />
                            )}
                            {isGoogleCal && (
                                <Chip
                                    icon={
                                        <Box
                                            component="img"
                                            src={GoogleCalendarIcon}
                                            sx={{ width: 14, height: 14 }}
                                        />
                                    }
                                    label="Google Calendar"
                                    size="small"
                                    variant="outlined"
                                    sx={{
                                        height: 22,
                                        fontSize: "0.7rem",
                                        borderColor: "rgba(0,0,0,0.12)"
                                    }}
                                />
                            )}
                            {duration > 0 && (
                                <Chip
                                    label={`${duration} min`}
                                    size="small"
                                    variant="outlined"
                                    sx={{
                                        height: 22,
                                        fontSize: "0.7rem",
                                        borderColor: "rgba(0,0,0,0.12)",
                                        color: "text.secondary"
                                    }}
                                />
                            )}
                            {isCourse && (
                                <Chip
                                    label="Course"
                                    size="small"
                                    sx={{
                                        height: 22,
                                        fontSize: "0.7rem",
                                        fontWeight: 600,
                                        backgroundColor: "#E8F5E9",
                                        color: "#2E7D32"
                                    }}
                                />
                            )}
                        </Box>
                    </Box>

                    <IconButton
                        size="small"
                        onClick={onClose}
                        sx={{
                            color: "text.disabled",
                            "&:hover": {
                                backgroundColor: "rgba(0,0,0,0.04)"
                            }
                        }}
                    >
                        <CloseIcon sx={{ fontSize: 18 }} />
                    </IconButton>
                </Box>
            </Box>

            {/* Body */}
            <Box sx={{ px: 2.5, pb: 2 }}>
                {/* Date & Time */}
                <InfoRow icon={AccessTimeIcon}>
                    <Typography variant="body2" fontWeight={500}>
                        {formatDateNice(event.date || event.startDate)}
                    </Typography>
                    <Typography
                        variant="body2"
                        color="text.secondary"
                        sx={{ fontSize: "0.825rem" }}
                    >
                        {formatTime12h(event.startTime)}
                        {event.endTime
                            ? ` – ${formatTime12h(event.endTime)}`
                            : ""}
                    </Typography>
                </InfoRow>

                {/* Client (for regular bookings) */}
                {!isBlocked && !isGoogleCal && event.clientName && (
                    <InfoRow icon={PersonOutlineIcon}>
                        <Typography
                            variant="body2"
                            fontWeight={500}
                            sx={{ lineHeight: 1.3 }}
                        >
                            {event.clientName}
                        </Typography>
                        {(event.clientPhone || event.clientEmail) && (
                            <Box sx={{ mt: 0.25 }}>
                                {event.clientPhone && (
                                    <Box
                                        component="a"
                                        href={`tel:${event.clientPhone}`}
                                        sx={{
                                            display: "flex",
                                            alignItems: "center",
                                            gap: 0.5,
                                            mb: 0.25,
                                            textDecoration: "none",
                                            color: "primary.main",
                                            "&:hover": { opacity: 0.8 }
                                        }}
                                    >
                                        <PhoneOutlinedIcon
                                            sx={{ fontSize: 13 }}
                                        />
                                        <Typography
                                            variant="caption"
                                            sx={{ textDecoration: "underline" }}
                                        >
                                            {event.clientPhone}
                                        </Typography>
                                    </Box>
                                )}
                                {event.clientEmail && (
                                    <Box
                                        component="a"
                                        href={`mailto:${event.clientEmail}`}
                                        sx={{
                                            display: "flex",
                                            alignItems: "center",
                                            gap: 0.5,
                                            textDecoration: "none",
                                            color: "primary.main",
                                            "&:hover": { opacity: 0.8 }
                                        }}
                                    >
                                        <EmailOutlinedIcon
                                            sx={{ fontSize: 13 }}
                                        />
                                        <Typography
                                            variant="caption"
                                            sx={{ textDecoration: "underline" }}
                                        >
                                            {event.clientEmail}
                                        </Typography>
                                    </Box>
                                )}
                            </Box>
                        )}
                    </InfoRow>
                )}

                {/* Location */}
                {(event.location ||
                    (isGoogleCal && event.MetaData?.address)) && (
                    <InfoRow icon={LocationOnOutlinedIcon}>
                        <Typography
                            component="a"
                            href={`https://www.google.com/maps/search/?api=1&query=${encodeURIComponent(
                                isGoogleCal
                                    ? event.MetaData?.address || ""
                                    : event.location || ""
                            )}`}
                            target="_blank"
                            rel="noopener noreferrer"
                            variant="body2"
                            color="primary"
                            sx={{
                                fontSize: "0.825rem",
                                textDecoration: "underline",
                                "&:hover": { opacity: 0.8 }
                            }}
                        >
                            {isGoogleCal
                                ? event.MetaData?.address
                                : event.location}
                        </Typography>
                    </InfoRow>
                )}

                {/* Course info */}
                {event.courseInfo && (
                    <InfoRow icon={SchoolOutlinedIcon}>
                        <Typography
                            variant="body2"
                            sx={{ fontSize: "0.825rem" }}
                        >
                            Session {event.courseInfo.sessionNo} of{" "}
                            {event.courseInfo.numberOfSessions}
                        </Typography>
                    </InfoRow>
                )}

                {/* Notes - always show for bookings so staff can see at a glance */}
                {!isGoogleCal && !isCourse && (
                    <InfoRow icon={NotesOutlinedIcon}>
                        {(() => {
                            const displayNotes = isBlocked
                                ? event.notes
                                : notesText;
                            if (!displayNotes) {
                                return (
                                    <Typography
                                        variant="body2"
                                        color="text.disabled"
                                        sx={{
                                            fontSize: "0.825rem",
                                            fontStyle: "italic"
                                        }}
                                    >
                                        No notes
                                    </Typography>
                                );
                            }
                            return (
                                <Typography
                                    variant="body2"
                                    color="text.secondary"
                                    sx={{
                                        fontSize: "0.825rem",
                                        lineHeight: 1.5,
                                        whiteSpace: "pre-line",
                                        maxHeight: 180,
                                        overflowY: "auto",
                                        pr: 0.5
                                    }}
                                >
                                    {displayNotes}
                                </Typography>
                            );
                        })()}
                    </InfoRow>
                )}

                {/* Google Calendar sync note */}
                {isGoogleCal && (
                    <Typography
                        variant="caption"
                        color="text.disabled"
                        sx={{
                            display: "block",
                            mt: 0.5,
                            fontSize: "0.7rem",
                            fontStyle: "italic"
                        }}
                    >
                        Synced from Google Calendar
                    </Typography>
                )}
            </Box>
        </Popover>
    );
}
